// Scan-metric view-state derivation for the Associate scan chart.
//
// Ported from v1 `AssociateScanReducer` RECEIVE_DATA (InductVisibilityWebsite-1.0). Extracted as
// pure functions so the migrated `AssociateScanChartView` and its unit tests share ONE source of
// truth for the three behaviors v1 performed when scan data arrived:
//   1. derive a running-sum `cumulative` granularity bucket from the `15m` bucket;
//   2. build the selectable associate list as the SORTED emails of the first granularity bucket;
//   3. auto-select every associate on the FIRST load, and preserve the user's still-valid
//      selection on subsequent polls (v1: `isFirstLoad ? newAssociateKeys : existingSelection`).
//
// Regression guard: the migrated view previously left `selectedAssociates` at `[]` forever, so the
// chart rendered zero series (empty chart, unchecked boxes) — a behavioral-parity gap vs the
// Original variant, which auto-selects all associates on first load. Keep these helpers aligned
// with v1 AssociateScanReducer.

import { ScanMetricMap, ScanDataPoint } from './data/associate-api';

export interface AssociateOption {
  key: string;
  name: string;
}

export interface ScanViewState {
  scanMetrics: ScanMetricMap;
  associateList: AssociateOption[];
  selectedAssociates: string[];
}

/**
 * Return a copy of `scanData` with a derived `cumulative` bucket: for each associate in the `15m`
 * bucket, a running sum of `metricValue` over time (matches v1 RECEIVE_DATA). If there is no
 * populated `15m` bucket, `scanData` is returned unchanged.
 */
export function withCumulative(scanData: ScanMetricMap): ScanMetricMap {
  const base = scanData['15m'];
  if (!base || Object.keys(base).length === 0) return scanData;

  const cumulative: Record<string, ScanDataPoint[]> = {};
  Object.keys(base).forEach((email) => {
    const data15m = base[email];
    if (Array.isArray(data15m)) {
      let cumulativeSum = 0;
      cumulative[email] = data15m.map((item) => {
        cumulativeSum += item.metricValue || 0;
        return { timeStampVal: item.timeStampVal, metricValue: cumulativeSum };
      });
    }
  });
  return { ...scanData, cumulative };
}

/**
 * Selectable associate options: the SORTED emails of the first granularity bucket, humanized by
 * stripping `@amazon.com` (matches v1 ordering). Returns `[]` when there is no data.
 */
export function deriveAssociateList(scanData: ScanMetricMap): AssociateOption[] {
  const firstGranularity = Object.keys(scanData)[0];
  if (!firstGranularity || !scanData[firstGranularity]) return [];
  return Object.keys(scanData[firstGranularity])
    .sort()
    .map((email) => ({ key: email, name: email.replace('@amazon.com', '') }));
}

/**
 * Reduce a freshly-fetched scan response into the next view state, mirroring v1
 * AssociateScanReducer RECEIVE_DATA:
 *   - derive the `cumulative` bucket,
 *   - build the sorted associate list,
 *   - auto-select ALL associates on first load (previous list empty), otherwise preserve the
 *     previous selection filtered to associates still present.
 */
export function deriveScanState(
  raw: ScanMetricMap,
  prevAssociateList: AssociateOption[],
  prevSelected: string[],
): ScanViewState {
  const scanMetrics = withCumulative({ ...(raw || {}) });
  const associateList = deriveAssociateList(scanMetrics);
  const newAssociateKeys = associateList.map((associate) => associate.key);
  const isFirstLoad = prevAssociateList.length === 0;
  const selectedAssociates = isFirstLoad
    ? newAssociateKeys
    : prevSelected.filter((key) => newAssociateKeys.includes(key));
  return { scanMetrics, associateList, selectedAssociates };
}
